import type {
  Canvass,
  CanvassHouse,
  CanvassListItem,
  CanvassParticipationState,
  CanvassStats,
  PublicCanvass,
} from "@/canvasses/types";
import type { User } from "@/users/types";
import { pick } from "lodash-es";
import { apiClient } from "./apiClient";

function toCanvassRequest(canvass: Canvass) {
  return {
    ...pick(canvass, [
      "id",
      "name",
      "conversationGuide",
      "abstract",
      "meetingPoint",
      "turfCount",
    ]),
    areaId: canvass.area.id,
    canvassTargets: canvass.canvassTargetsWithProgress.map((target) =>
      pick(target, ["id", "description", "target"]),
    ),
  };
}

export const canvassesGateway = {
  async listCanvasses(houseId?: number): Promise<Array<CanvassListItem>> {
    if (houseId !== undefined) {
      return await apiClient.get(`/api/canvasses?house-id=${houseId}`);
    }

    return await apiClient.get("/api/canvasses");
  },

  async getCanvass(id: string): Promise<Canvass> {
    return await apiClient.get(`/api/canvasses/${id}`);
  },

  async createCanvass(canvass: Canvass): Promise<Canvass> {
    return await apiClient.post("/api/canvasses", toCanvassRequest(canvass));
  },

  async updateCanvass(canvass: Canvass): Promise<Canvass> {
    return await apiClient.put(
      `/api/canvasses/${canvass.id}`,
      toCanvassRequest(canvass),
    );
  },

  async deleteCanvass(canvassId: string): Promise<Canvass> {
    return await apiClient.delete(`/api/canvasses/${canvassId}`);
  },

  async getCanvassStats(id: string): Promise<CanvassStats> {
    return await apiClient.get(`/api/canvasses/${id}/stats`);
  },

  async publishCanvass(id: string): Promise<void> {
    return await apiClient.post(`/api/canvasses/${id}/publish`);
  },

  async unpublishCanvass(id: string): Promise<void> {
    return await apiClient.post(`/api/canvasses/${id}/unpublish`);
  },

  async participateInCanvass(
    id: string,
  ): Promise<{ participationState: CanvassParticipationState }> {
    return await apiClient.post(`/api/canvasses/${id}/participate`);
  },

  async findCanvassByShortUrlId(shortUrlId: string): Promise<PublicCanvass> {
    return await apiClient.get(`/api/canvasses/short-url/${shortUrlId}`);
  },

  async addParticipant(canvass: Canvass, user: User): Promise<void> {
    await apiClient.post(
      `/api/canvasses/${canvass.id}/participants/${user.id}`,
    );
  },

  async deleteParticipant(canvass: Canvass, user: User): Promise<void> {
    await apiClient.delete(
      `/api/canvasses/${canvass.id}/participants/${user.id}`,
    );
  },

  async listPendingParticipants(canvass: Canvass): Promise<Array<User>> {
    return await apiClient.get(
      `/api/canvasses/${canvass.id}/pending-participants`,
    );
  },

  async confirmPendingParticipant(canvass: Canvass, user: User): Promise<void> {
    return await apiClient.post(
      `/api/canvasses/${canvass.id}/pending-participants/${user.id}/confirm`,
    );
  },

  async deletePendingParticipant(canvass: Canvass, user: User): Promise<void> {
    return await apiClient.delete(
      `/api/canvasses/${canvass.id}/pending-participants/${user.id}`,
    );
  },
};

export const canvassHousesGateway = {
  async listCanvassesHouses(canvassId: string): Promise<Array<CanvassHouse>> {
    return await apiClient.get(`/api/canvasses/${canvassId}/houses`);
  },

  async getCanvassHouse(
    canvassId: string,
    houseId: number,
  ): Promise<CanvassHouse> {
    return await apiClient.get(`/api/canvasses/${canvassId}/houses/${houseId}`);
  },

  async updateHouse(
    canvassId: string,
    canvassHouse: CanvassHouse,
  ): Promise<void> {
    return await apiClient.put(
      `/api/canvasses/${canvassId}/houses/${canvassHouse.id}`,
      pick(canvassHouse, [
        "completed",
        "doorsKnocked",
        "doorsOpened",
        "numberOfFloors",
        "numberOfDoors",
        "yearBuilt",
        "heatingType",
        "owner",
        "tags",
      ]),
    );
  },

  async updateCanvassHouse(
    canvassId: string,
    canvassHouse: CanvassHouse,
  ): Promise<void> {
    return await apiClient.put(
      `/api/canvasses/${canvassId}/canvassHouses/${canvassHouse.id}`,
      pick(canvassHouse, ["completed", "doorsKnocked", "doorsOpened"]),
    );
  },
};
